import { useEffect, useMemo } from "react";
import { match } from "ts-pattern";
import { CountryCode, useGetAttributeProductsQuery } from "../../generated/storefront";
import { Frame } from "../../types/product";
import { preloadImage } from "../../helpers/layout";
import { CheckoutState } from "./SideBar";
import { getSizeAndFrame } from "../../helpers/product";

type Props = {
  state: CheckoutState;
  selectedCountry: CountryCode;
};

const useAttributes = ({ state, selectedCountry }: Props) => {
  const { data: attributeProductsData, refetch } = useGetAttributeProductsQuery({
    variables: { country: selectedCountry },
  });
  const { standardFrameProduct, premiumFrameProduct, mountingProduct, canvasProduct } = attributeProductsData || {};

  const frameVariant = standardFrameProduct?.variants.nodes.find((variant) => variant.selectedOptions[0].value === state.size);
  const framePrice = state.frame === Frame.Unframed ? 0 : Number(frameVariant?.price.amount || 0);

  const mountingVariant = mountingProduct?.variants.nodes.find((variant) => variant.selectedOptions[0].value === state.size);
  const mountingPrice = Number(mountingVariant?.price.amount || 0);

  const boxFrameVariant = premiumFrameProduct?.variants.nodes.find((variant) => variant.selectedOptions[0].value === state.size);
  const boxFramePrice = Number(boxFrameVariant?.price.amount || 0);
  const boxFramePriceDiff = boxFramePrice - framePrice || 0;

  const canvasVariant = canvasProduct?.variants.nodes.find((variant) => {
    const { size, frame } = getSizeAndFrame(variant.selectedOptions);
    return size === state.size && frame === state.frame;
  });
  const canvasPrice = Number(canvasVariant?.price.amount || 0);

  // SWATCHES
  const standardFrameSwatches = useMemo(() => {
    const naturalSwatchImage = standardFrameProduct?.images.nodes.find((image) => image.src.includes("natural_swatch"));
    const blackSwatchImage = standardFrameProduct?.images.nodes.find((image) => image.src.includes("black_swatch"));
    const whiteSwatchImage = standardFrameProduct?.images.nodes.find((image) => image.src.includes("white_swatch"));
    return { naturalSwatchImage, blackSwatchImage, whiteSwatchImage };
  }, [standardFrameProduct]);

  const standardFrameSwatch = useMemo(() => {
    if (!state.frame || state.frame === Frame.Unframed) return null;
    const { blackSwatchImage, whiteSwatchImage, naturalSwatchImage } = standardFrameSwatches;
    return match(state.frame)
      .with(Frame.Black, () => blackSwatchImage)
      .with(Frame.White, () => whiteSwatchImage)
      .with(Frame.Natural, () => naturalSwatchImage)
      .exhaustive();
  }, [state.frame, standardFrameSwatches]);

  const boxFrameSwatches = useMemo(() => {
    const naturalSwatchImage = premiumFrameProduct?.images.nodes.find((image) => image.src.includes("natural_premium_swatch"));
    const blackSwatchImage = premiumFrameProduct?.images.nodes.find((image) => image.src.includes("black_premium_swatch"));
    const whiteSwatchImage = premiumFrameProduct?.images.nodes.find((image) => image.src.includes("white_premium_swatch"));
    return { naturalSwatchImage, blackSwatchImage, whiteSwatchImage };
  }, [premiumFrameProduct]);

  const boxFrameSwatch = useMemo(() => {
    if (!state.frame || state.frame === Frame.Unframed) return null;
    const { blackSwatchImage, whiteSwatchImage, naturalSwatchImage } = boxFrameSwatches;
    return match(state.frame)
      .with(Frame.Black, () => blackSwatchImage)
      .with(Frame.White, () => whiteSwatchImage)
      .with(Frame.Natural, () => naturalSwatchImage)
      .exhaustive();
  }, [state.frame, boxFrameSwatches]);

  const mountSwatches = useMemo(() => {
    const naturalSwatchImage = mountingProduct?.images.nodes.find((image) => image.src.includes("natural_mount_swatch"));
    const blackSwatchImage = mountingProduct?.images.nodes.find((image) => image.src.includes("black_mount_swatch"));
    const whiteSwatchImage = mountingProduct?.images.nodes.find((image) => image.src.includes("white_mount_swatch"));
    const noMountNaturalSwatchImage = mountingProduct?.images.nodes.find((image) => image.src.includes("natural_nomount_swatch"));
    const noMountBlackSwatchImage = mountingProduct?.images.nodes.find((image) => image.src.includes("black_nomount_swatch"));
    const noMountWhiteSwatchImage = mountingProduct?.images.nodes.find((image) => image.src.includes("white_nomount_swatch"));

    return {
      naturalSwatchImage,
      blackSwatchImage,
      whiteSwatchImage,
      noMountNaturalSwatchImage,
      noMountBlackSwatchImage,
      noMountWhiteSwatchImage,
    };
  }, [mountingProduct]);

  const mountSwatch = useMemo(() => {
    const { blackSwatchImage, whiteSwatchImage, naturalSwatchImage } = mountSwatches;
    return match(state.frame)
      .with(Frame.Black, () => blackSwatchImage)
      .with(Frame.White, () => whiteSwatchImage)
      .otherwise(() => naturalSwatchImage);
  }, [state.frame, mountSwatches]);

  const noMountSwatch = useMemo(() => {
    const { noMountNaturalSwatchImage, noMountBlackSwatchImage, noMountWhiteSwatchImage } = mountSwatches;
    return match(state.frame)
      .with(Frame.Black, () => noMountBlackSwatchImage)
      .with(Frame.White, () => noMountWhiteSwatchImage)
      .otherwise(() => noMountNaturalSwatchImage);
  }, [state.frame, mountSwatches]);

  const canvasSwatches = useMemo(() => {
    const unframed = canvasProduct?.images.nodes.find((image) => image.src.includes("unframed_canvas_swatch"));
    const natural = canvasProduct?.images.nodes.find((image) => image.src.includes("natural_canvas_swatch"));
    const black = canvasProduct?.images.nodes.find((image) => image.src.includes("black_canvas_swatch"));
    const white = canvasProduct?.images.nodes.find((image) => image.src.includes("white_canvas_swatch"));
    return { unframed, natural, black, white } as const;
  }, [canvasProduct]);

  const availabileFrames = useMemo(
    () => standardFrameProduct?.options.find((option) => option.name === "color")?.optionValues || [],
    [standardFrameProduct]
  );

  useEffect(() => {
    refetch();
  }, [selectedCountry, refetch]);

  useEffect(() => {
    if (standardFrameSwatches.naturalSwatchImage?.src) preloadImage(standardFrameSwatches.naturalSwatchImage.src);
    if (standardFrameSwatches.blackSwatchImage?.src) preloadImage(standardFrameSwatches.blackSwatchImage.src);
    if (standardFrameSwatches.whiteSwatchImage?.src) preloadImage(standardFrameSwatches.whiteSwatchImage.src);
  }, [standardFrameSwatches]);

  useEffect(() => {
    if (boxFrameSwatches.naturalSwatchImage?.src) preloadImage(boxFrameSwatches.naturalSwatchImage.src);
    if (boxFrameSwatches.blackSwatchImage?.src) preloadImage(boxFrameSwatches.blackSwatchImage.src);
    if (boxFrameSwatches.whiteSwatchImage?.src) preloadImage(boxFrameSwatches.whiteSwatchImage.src);
  }, [boxFrameSwatches]);

  useEffect(() => {
    if (mountSwatches.naturalSwatchImage?.src) preloadImage(mountSwatches.naturalSwatchImage.src);
    if (mountSwatches.blackSwatchImage?.src) preloadImage(mountSwatches.blackSwatchImage.src);
    if (mountSwatches.whiteSwatchImage?.src) preloadImage(mountSwatches.whiteSwatchImage.src);
    if (mountSwatches.noMountNaturalSwatchImage?.src) preloadImage(mountSwatches.noMountNaturalSwatchImage.src);
    if (mountSwatches.noMountBlackSwatchImage?.src) preloadImage(mountSwatches.noMountBlackSwatchImage.src);
    if (mountSwatches.noMountWhiteSwatchImage?.src) preloadImage(mountSwatches.noMountWhiteSwatchImage.src);
  }, [mountSwatches]);

  useEffect(() => {
    if (canvasSwatches.unframed?.src) preloadImage(canvasSwatches.unframed.src);
    if (canvasSwatches.natural?.src) preloadImage(canvasSwatches.natural.src);
    if (canvasSwatches.black?.src) preloadImage(canvasSwatches.black.src);
    if (canvasSwatches.white?.src) preloadImage(canvasSwatches.white.src);
  }, [canvasSwatches]);

  return {
    mountingPrice,
    framePrice,
    boxFramePrice,
    boxFramePriceDiff,
    availabileFrames,
    standardFrameSwatch,
    boxFrameSwatch,
    mountSwatch,
    noMountSwatch,
    canvasPrice,
    canvasSwatches,
  };
};
export default useAttributes;
